<script setup lang="ts">
import { computed } from 'vue';

import type { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters';
import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import type { AccommodationSelectorType } from '@apps/main/new-desktop/modules/accommodationSelector';
import { AccommodationSelector } from '@apps/main/new-desktop/modules/accommodationSelector';

import ItineraryTimeline from '../ItineraryTimeline.vue';
import { ProductCardTopBarType } from '../../../models';
import type { AlternativeRoomSuggestionAccommodationSegment } from '../../../models';
import ChangeProductLink from '../ChangeProductLink.vue';

import AccommodationAlternativeProductCard from './AccommodationAlternativeProductCard.vue';
import AccommodationSegmentHeader from './AccommodationSegmentHeader.vue';

interface AlternativeRoomSuggestionAccommodationSegmentProps {
  segment: AlternativeRoomSuggestionAccommodationSegment;
}

const props = defineProps<AlternativeRoomSuggestionAccommodationSegmentProps>();

interface AlternativeRoomSuggestionAccommodationSegmentEmits {
  (eventName: 'add-filter'): void;
  (eventName: 'change-hotel'): void;
  (eventName: 'confirm-alternative'): void;
  (eventName: 'hotel-selection-navigation-triggered'): void;
  (eventName: 'remove-filter', event: { type: HotelFilter; id?: string }): void;
  (eventName: 'show-details'): void;
  (eventName: 'show-more-filters'): void;
  (eventName: 'show-rooms'): void;
  (eventName: 'suggestion-type-selected', suggestionType: SuggestionType): void;
  (eventName: 'toggle-accommodation', value: AccommodationSelectorType): void;
}

const emit = defineEmits<AlternativeRoomSuggestionAccommodationSegmentEmits>();

//NOTE: duplicated
const currentSelectedOrderType = computed<
  AccommodationSelectorType | undefined
>(() => {
  if (props.segment.barType === ProductCardTopBarType.Selected) {
    return;
  }

  return props.segment.barType === ProductCardTopBarType.Suggestion
    ? 'popularAccommodation'
    : 'cheapestAccommodation';
});
</script>

<template>
  <ItineraryTimeline
    :segment-type="props.segment.segmentType"
    class="alternative-room-suggestion-accommodation-segment"
  >
    <template #dropdown>
      <AccommodationSelector
        v-if="currentSelectedOrderType"
        :selected-option="currentSelectedOrderType"
        :disabled="false"
        @toggle-accommodation="emit('toggle-accommodation', $event)"
      />
    </template>

    <template #header>
      <AccommodationSegmentHeader
        :segment="props.segment"
        :print-mode="false"
        @suggestion-type-selected="emit('suggestion-type-selected', $event)"
        @add-filter="emit('add-filter')"
        @show-more-filters="emit('show-more-filters')"
        @remove-filter="emit('remove-filter', $event)"
      />
    </template>

    <template #content>
      <div class="content-wrapper">
        <AccommodationAlternativeProductCard
          :product-card-data="props.segment.productCardData"
          :context="props.segment.context"
          @confirm-alternative="emit('confirm-alternative')"
          @show-details="emit('show-details')"
          @show-rooms="emit('show-rooms')"
          @change-hotel="emit('change-hotel')"
          @navigation-triggered="emit('hotel-selection-navigation-triggered')"
        />
        <ChangeProductLink
          :link-context="{
            segmentType: props.segment.segmentType,
            ingredientIndex: props.segment.context.ingredientIndex,
          }"
          class="change-product-link"
        />
      </div>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.alternative-room-suggestion-accommodation-segment {
  .content-wrapper {
    display: flex;
    flex-flow: column;
    gap: var(--spacing-12);

    .change-product-link {
      align-self: flex-end;
    }
  }
}
</style>
